import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  Car,
  Stethoscope,
  Briefcase,
  Search,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Download,
  RefreshCw,
  Users,
} from 'lucide-react';
import '../styles/leads.css';

const LeadsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('baride');
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ baride_total: 0, care365_total: 0, offerings_total: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [activeTab, page, search]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leads/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Failed to fetch lead stats:', err);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.set('search', search);
      const res = await fetch(`${API_URL}/api/leads/admin/${activeTab}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      const res = await fetch(`${API_URL}/api/leads/admin/${activeTab}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setLeads((prev) => prev.filter((l) => l.id !== id));
        fetchStats();
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const exportCSV = () => {
    if (!leads.length) return;
    const headers = Object.keys(leads[0]);
    const csv = [
      headers.join(','),
      ...leads.map((l) => headers.map((h) => `"${(l[h] ?? '').toString().replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_leads.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-leads-page" className="leads-page">
        {/* Header */}
        <div className="leads-header">
          <div>
            <h2>Lead Management</h2>
            <p>View and manage submissions from BaRide, Care365 and Offerings</p>
          </div>
          <div className="leads-actions">
            <button data-testid="leads-export-csv" className="action-btn" onClick={exportCSV}>
              <Download size={16} /> Export CSV
            </button>
            <button data-testid="leads-refresh" className="action-btn primary" onClick={() => { fetchLeads(); fetchStats(); }}>
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="leads-stats-row">
          <div className="lead-stat-card baride">
            <Car size={22} />
            <div>
              <span className="stat-value" data-testid="baride-lead-count">{stats.baride_total}</span>
              <span className="stat-label">BaRide Drivers</span>
            </div>
          </div>
          <div className="lead-stat-card care365">
            <Stethoscope size={22} />
            <div>
              <span className="stat-value" data-testid="care365-lead-count">{stats.care365_total}</span>
              <span className="stat-label">Care365 Doctors</span>
            </div>
          </div>
          <div className="lead-stat-card care365">
            <Briefcase size={22} />
            <div>
              <span className="stat-value" data-testid="offerings-lead-count">{stats.offerings_total}</span>
              <span className="stat-label">Offerings Enquiries</span>
            </div>
          </div>
          <div className="lead-stat-card total">
            <Users size={22} />
            <div>
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Leads</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="leads-tabs">
          <button
            data-testid="leads-tab-baride"
            className={`leads-tab ${activeTab === 'baride' ? 'active' : ''}`}
            onClick={() => { setActiveTab('baride'); setPage(1); setSearch(''); }}
          >
            <Car size={16} /> BaRide Leads
          </button>
          <button
            data-testid="leads-tab-care365"
            className={`leads-tab ${activeTab === 'care365' ? 'active' : ''}`}
            onClick={() => { setActiveTab('care365'); setPage(1); setSearch(''); }}
          >
            <Stethoscope size={16} /> Care365 Leads
          </button>
          <button
            data-testid="leads-tab-offerings"
            className={`leads-tab ${activeTab === 'offerings' ? 'active' : ''}`}
            onClick={() => { setActiveTab('offerings'); setPage(1); setSearch(''); }}
          >
            <Briefcase size={16} /> Offerings Leads
          </button>
        </div>

        {/* Search */}
        <div className="leads-search">
          <Search size={18} className="search-icon" />
          <input
            data-testid="leads-search-input"
            type="text"
            placeholder={activeTab === 'baride' ? 'Search by name or phone...' : 'Search by name, hospital or phone...'}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        {/* Table */}
        <div className="leads-table-wrap">
          {loading ? (
            <div className="leads-loading">
              <div className="loading-spinner" />
              <p>Loading leads...</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="leads-empty">
              <p>No leads found{search ? ` for "${search}"` : ''}.</p>
            </div>
          ) : activeTab === 'baride' ? (
            <table className="leads-table" data-testid="baride-leads-table">
              <thead>
                <tr>
                  <th>Driver Name</th>
                  <th>Phone</th>
                  <th>Vehicle</th>
                  <th>City</th>
                  <th>Country</th>
                  <th>Referred</th>
                  <th>Referrer</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td className="name-cell">{l.driver_name}</td>
                    <td><Phone size={13} className="inline mr-1 text-gray-400" />{l.phone}</td>
                    <td><span className="badge vehicle">{l.vehicle_type}</span></td>
                    <td>{l.city || '-'}</td>
                    <td>{l.country || '-'}</td>
                    <td>{l.is_referred ? <span className="badge yes">Yes</span> : <span className="badge no">No</span>}</td>
                    <td>{l.referrer_name || '-'}</td>
                    <td className="date-cell">{formatDate(l.created_at)}</td>
                    <td>
                      <button className="delete-btn" onClick={() => handleDelete(l.id)} title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : activeTab === 'care365' ? (
            <table className="leads-table" data-testid="care365-leads-table">
              <thead>
                <tr>
                  <th>Doctor Name</th>
                  <th>Specialty</th>
                  <th>Hospital</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>City</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td className="name-cell">{l.doctor_name}</td>
                    <td>{l.title}</td>
                    <td><Building2Icon /> {l.hospital}</td>
                    <td><Phone size={13} className="inline mr-1 text-gray-400" />{l.phone}</td>
                    <td>{l.email ? <><Mail size={13} className="inline mr-1 text-gray-400" />{l.email}</> : '-'}</td>
                    <td><MapPin size={13} className="inline mr-1 text-gray-400" />{l.city}</td>
                    <td className="date-cell">{formatDate(l.created_at)}</td>
                    <td>
                      <button className="delete-btn" onClick={() => handleDelete(l.id)} title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="leads-table" data-testid="offerings-leads-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Offerings</th>
                  <th>Services</th>
                  <th>Prefers</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td className="name-cell">{l.full_name}</td>
                    <td>{l.company}</td>
                    <td>
                      <div className="text-xs"><Phone size={12} className="inline mr-1 text-gray-400" />{l.phone}</div>
                      <div className="text-xs"><Mail size={12} className="inline mr-1 text-gray-400" />{l.email}</div>
                    </td>
                    <td>{(l.offerings || []).join(', ') || '-'}</td>
                    <td style={{ maxWidth: 240 }}>
                      <span className="text-xs">{(l.services || []).length ? `${l.services.length}: ${l.services.slice(0, 3).join(', ')}${l.services.length > 3 ? '…' : ''}` : '-'}</span>
                    </td>
                    <td><span className="badge vehicle">{l.preferred_contact}</span></td>
                    <td className="date-cell">{formatDate(l.created_at)}</td>
                    <td>
                      <button className="delete-btn" onClick={() => handleDelete(l.id)} title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="leads-pagination">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="leads-prev-page">
              <ChevronLeft size={16} /> Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="leads-next-page">
              Next <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Small helper so we don't import Building2 from lucide just for the table
const Building2Icon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline mr-1 text-gray-400"><rect width="8" height="18" x="3" y="4" rx="1"/><path d="M15 2h2a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-2"/><path d="M7 8h0"/><path d="M7 12h0"/><path d="M7 16h0"/></svg>
);

export default LeadsPage;
